import { useEffect, useState, useMemo } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { CalendarDays, Clock, User, CheckCircle, Video, Phone, XCircle, RotateCcw, ChevronDown, ShieldCheck, BellRing, Loader2 } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { parseISO, isBefore } from "date-fns";
import { useAuditLog } from "@/hooks/useAuditLog";
import PatientConsultDrawer from "@/components/PatientConsultDrawer";
import { getBrowserTimezone, getTimezoneAbbr, formatInTz } from "@/lib/timezone";
import { buildGoogleCalendarUrl, buildIcsDataUrl, buildConsultationEventStrings } from "@/lib/calendar-links";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";

interface AppointmentWithPatient {
  id: string;
  patient_id: string;
  start_time: string;
  end_time: string;
  status: string;
  notes: string | null;
  meeting_url: string | null;
  call_type: string;
  patient_first_name?: string | null;
  patient_last_name?: string | null;
}

const statusConfig: Record<string, { label: string; class: string }> = {
  scheduled: { label: "Upcoming", class: "bg-primary/10 text-primary border-primary/20" },
  confirmed: { label: "Confirmed", class: "bg-emerald-100 text-emerald-800 border-emerald-200" },
  cancelled: { label: "Cancelled", class: "bg-muted text-muted-foreground border-border" },
  completed: { label: "Completed", class: "bg-green-100 text-green-800 border-green-200" },
  no_show: { label: "No-Show", class: "bg-red-100 text-red-800 border-red-200" },
  rescheduled: { label: "Rescheduled", class: "bg-blue-100 text-blue-800 border-blue-200" },
  pending: { label: "Pending", class: "bg-yellow-100 text-yellow-800 border-yellow-200" },
};

const ProviderAppointments = () => {
  const { user } = useAuth();
  const { toast } = useToast();
  const [appointments, setAppointments] = useState<AppointmentWithPatient[]>([]);
  const [drawerPatientId, setDrawerPatientId] = useState<string | null>(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [noShowConfirmId, setNoShowConfirmId] = useState<string | null>(null);
  const [completeConfirmId, setCompleteConfirmId] = useState<string | null>(null);
  const [rescheduleConfirmId, setRescheduleConfirmId] = useState<string | null>(null);
  const [cancelConfirmId, setCancelConfirmId] = useState<string | null>(null);
  const [cancelReason, setCancelReason] = useState("");
  const [nudgingId, setNudgingId] = useState<string | null>(null);

  const nudgePatient = async (appt: AppointmentWithPatient) => {
    setNudgingId(appt.id);
    try {
      const patientName = appt.patient_first_name || "there";
      const { data: providerProfile } = await supabase
        .from("profiles")
        .select("first_name, last_name, title_prefix")
        .eq("user_id", user?.id || "")
        .maybeSingle();
      const providerName = providerProfile
        ? `${providerProfile.title_prefix ? providerProfile.title_prefix + " " : ""}${providerProfile.last_name || providerProfile.first_name || "your provider"}`.trim()
        : undefined;

      const { error } = await supabase.functions.invoke("send-transactional-email", {
        body: {
          templateName: "schedule-consultation-reminder",
          recipientUserId: appt.patient_id,
          idempotencyKey: `nudge-reschedule-${appt.id}-${new Date().toISOString().slice(0, 10)}`,
          notificationCategory: "appointment_reminders",
          templateData: { patientName, providerName },
        },
      });
      if (error) throw error;

      await supabase.from("notifications").insert({
        user_id: appt.patient_id,
        title: "Reminder: pick a new appointment time",
        message: "Your provider sent you a reminder to reschedule your consultation.",
        type: "appointment_nudge",
        related_id: appt.id,
      });

      logAccess({ action: "nudge_patient_reschedule", resource_type: "appointment", resource_id: appt.id, metadata: { patient_id: appt.patient_id } });
      toast({ title: "Reminder sent", description: `${patientName} has been nudged to pick a new time.` });
    } catch (err: any) {
      toast({ title: "Couldn't send nudge", description: err?.message || "Please try again.", variant: "destructive" });
    } finally {
      setNudgingId(null);
    }
  };
  const { logAccess } = useAuditLog();

  const tz = useMemo(() => getBrowserTimezone(), []);
  const tzAbbr = useMemo(() => getTimezoneAbbr(tz), [tz]);

  useEffect(() => {
    if (!user) return;
    fetchAppointments();
  }, [user]);

  const fetchAppointments = async () => {
    if (!user) return;

    const { data } = await supabase
      .from("appointments")
      .select("id, patient_id, start_time, end_time, status, notes, meeting_url, call_type")
      .eq("provider_id", user.id)
      .order("start_time", { ascending: true });

    if (!data || data.length === 0) {
      setAppointments([]);
      return;
    }

    const patientIds = [...new Set(data.map((a: any) => a.patient_id))];
    const { data: profiles } = await supabase
      .from("profiles")
      .select("user_id, first_name, last_name")
      .in("user_id", patientIds);

    const profileMap = new Map(profiles?.map((p: any) => [p.user_id, p]) || []);

    setAppointments(
      data.map((a: any) => {
        const profile = profileMap.get(a.patient_id);
        return {
          ...a,
          patient_first_name: profile?.first_name,
          patient_last_name: profile?.last_name,
        };
      })
    );
    logAccess({ action: "view_appointments", resource_type: "appointment", metadata: { count: data.length, role: "provider" } });
    for (const pid of patientIds) {
      logAccess({ action: "view_patient_profile", resource_type: "profile", resource_id: pid });
    }
  };

  const updateStatus = async (id: string, newStatus: string, reason?: string) => {
    const appt = appointments.find((a) => a.id === id);
    const trimmedReason = reason?.trim();
    const updatePayload: { status: string; notes?: string } = { status: newStatus };
    if (newStatus === "cancelled" && trimmedReason) {
      const stamp = new Date().toISOString();
      const reasonLine = `[Cancelled ${stamp}] Reason: ${trimmedReason}`;
      updatePayload.notes = appt?.notes ? `${appt.notes}\n\n${reasonLine}` : reasonLine;
    }
    const { error } = await supabase
      .from("appointments")
      .update(updatePayload)
      .eq("id", id);

    if (error) {
      toast({ title: "Update failed", description: error.message, variant: "destructive" });
      return;
    }

    logAccess({ action: newStatus === "completed" ? "complete_appointment" : newStatus === "no_show" ? "no_show_appointment" : newStatus === "confirmed" ? "confirm_appointment" : "scheduled_appointment", resource_type: "appointment", resource_id: id });

    if (newStatus === "confirmed") {
      toast({ title: "Appointment confirmed" });
      if (appt?.patient_id) {
        await supabase.from("notifications").insert({
          user_id: appt.patient_id,
          title: "Appointment Confirmed",
          message: "Your upcoming consultation has been confirmed by your provider.",
          type: "appointment_confirmed",
          related_id: id,
        });

        // Send confirmation email to patient
        const { data: patientEmails } = await supabase.rpc("get_user_emails");
        const patientEmail = patientEmails?.find((e: any) => e.user_id === appt.patient_id)?.email;
        if (patientEmail) {
          const { data: providerProfile } = await supabase
            .from("profiles")
            .select("first_name, last_name, title_prefix")
            .eq("user_id", user!.id)
            .single();
          const providerName = providerProfile
            ? `${providerProfile.title_prefix || "Dr."} ${providerProfile.first_name || ""} ${providerProfile.last_name || ""}`.trim()
            : "Your provider";
          const patientName = appt.patient_first_name || undefined;
          const startDate = appt.start_time ? formatInTz(appt.start_time, tz, "MMMM d, yyyy") : undefined;
          const startTime = appt.start_time && appt.end_time
            ? `${formatInTz(appt.start_time, tz, "h:mm a")} – ${formatInTz(appt.end_time, tz, "h:mm a")} ${tzAbbr}`
            : undefined;

          // Build Google Calendar + .ics links so the patient can re-add the
          // event from this confirmation email (mirrors the booking email).
          let googleCalendarUrl: string | undefined;
          let icsUrl: string | undefined;
          if (appt.start_time && appt.end_time && appt.meeting_url && startDate && startTime) {
            const event = buildConsultationEventStrings({
              providerName,
              callType: appt.call_type,
              meetingUrl: appt.meeting_url,
              appointmentDate: startDate,
              appointmentTime: startTime,
              dashboardUrl: `${window.location.origin}/dashboard`,
            });
            const startInstant = new Date(appt.start_time);
            const endInstant = new Date(appt.end_time);
            googleCalendarUrl = buildGoogleCalendarUrl({
              start: startInstant,
              end: endInstant,
              title: event.title,
              description: event.description,
              location: appt.meeting_url,
            });
            icsUrl = buildIcsDataUrl({
              start: startInstant,
              end: endInstant,
              title: event.title,
              description: event.description,
              location: appt.meeting_url,
            });
          }

          await supabase.functions.invoke("send-transactional-email", {
            body: {
              templateName: "appointment-confirmed",
              recipientEmail: patientEmail,
              idempotencyKey: `appt-confirmed-${id}`,
              templateData: {
                recipientName: patientName,
                providerName,
                appointmentDate: startDate,
                appointmentTime: startTime,
                callType: appt.call_type,
                meetingUrl: appt.meeting_url || undefined,
                googleCalendarUrl,
                icsUrl,
              },
            },
          });
        }
      }
    } else if (newStatus === "completed") {
      toast({ title: "Marked as completed" });
      if (appt?.patient_id) {
        await supabase.from("notifications").insert({
          user_id: appt.patient_id,
          title: "Consultation Completed",
          message: "Your consultation has been completed. Your provider will follow up with next steps regarding your treatment plan.",
          type: "consultation_completed",
          related_id: id,
        });

        // Send completion email to patient
        const { data: patientEmails } = await supabase.rpc("get_user_emails");
        const patientEmail = patientEmails?.find((e: any) => e.user_id === appt.patient_id)?.email;
        if (patientEmail) {
          const { data: providerProfile } = await supabase
            .from("profiles")
            .select("first_name, last_name, title_prefix")
            .eq("user_id", user!.id)
            .single();
          const providerName = providerProfile
            ? `${providerProfile.title_prefix || "Dr."} ${providerProfile.first_name || ""} ${providerProfile.last_name || ""}`.trim()
            : "Your provider";
          const patientName = appt.patient_first_name || undefined;
          const startDate = appt.start_time ? formatInTz(appt.start_time, tz, "MMMM d, yyyy") : undefined;
          const startTime = appt.start_time && appt.end_time
            ? `${formatInTz(appt.start_time, tz, "h:mm a")} – ${formatInTz(appt.end_time, tz, "h:mm a")} ${tzAbbr}`
            : undefined;

          await supabase.functions.invoke("send-transactional-email", {
            body: {
              templateName: "consultation-completed",
              recipientEmail: patientEmail,
              idempotencyKey: `consultation-completed-${id}`,
              templateData: {
                recipientName: patientName,
                providerName,
                appointmentDate: startDate,
                appointmentTime: startTime,
              },
            },
          });
        }
      }
    } else if (newStatus === "no_show") {
      toast({ title: "Marked as no-show" });
      if (appt?.patient_id) {
        await supabase.from("notifications").insert({
          user_id: appt.patient_id,
          title: "Missed Appointment",
          message: "You missed your scheduled consultation. Please reschedule at your earliest convenience.",
          type: "no_show",
          related_id: id,
        });
        const patientName = appt.patient_first_name
          ? `${appt.patient_first_name}${appt.patient_last_name ? ` ${appt.patient_last_name}` : ""}`
          : undefined;

        const { data: providerProfile } = await supabase
          .from("profiles")
          .select("first_name, last_name, title_prefix")
          .eq("user_id", user!.id)
          .single();
        const providerName = providerProfile
          ? `${providerProfile.title_prefix ? providerProfile.title_prefix + " " : ""}${providerProfile.last_name || providerProfile.first_name || "Your Provider"}`
          : "Your Provider";

        await supabase.functions.invoke("send-transactional-email", {
          body: {
            templateName: "appointment-no-show",
            recipientUserId: appt.patient_id,
            idempotencyKey: `no-show-reschedule-${id}`,
            templateData: {
              patientName,
              providerName,
              originalDate: formatInTz(appt.start_time, tz, "EEEE, MMMM d, yyyy"),
              originalTime: `${formatInTz(appt.start_time, tz, "h:mm a")} – ${formatInTz(appt.end_time, tz, "h:mm a")} ${tzAbbr}`,
            },
          },
        });
      }
    } else if (newStatus === "cancelled") {
      toast({ title: "Appointment cancelled" });
      if (appt?.patient_id) {
        await supabase.from("notifications").insert({
          user_id: appt.patient_id,
          title: "Appointment Cancelled",
          message: trimmedReason
            ? `Your scheduled consultation has been cancelled. Reason: ${trimmedReason}`
            : "Your scheduled consultation has been cancelled. Please contact our care team if you have any questions.",
          type: "appointment_cancelled",
          related_id: id,
        });

        const { data: patientEmails } = await supabase.rpc("get_user_emails");
        const patientEmail = patientEmails?.find((e: any) => e.user_id === appt.patient_id)?.email;
        if (patientEmail) {
          const { data: providerProfile } = await supabase
            .from("profiles")
            .select("first_name, last_name, title_prefix")
            .eq("user_id", user!.id)
            .single();
          const providerName = providerProfile
            ? `${providerProfile.title_prefix || "Dr."} ${providerProfile.first_name || ""} ${providerProfile.last_name || ""}`.trim()
            : "Your provider";
          const patientName = appt.patient_first_name || undefined;
          const startDate = appt.start_time ? formatInTz(appt.start_time, tz, "MMMM d, yyyy") : undefined;
          const startTime = appt.start_time && appt.end_time
            ? `${formatInTz(appt.start_time, tz, "h:mm a")} – ${formatInTz(appt.end_time, tz, "h:mm a")} ${tzAbbr}`
            : undefined;

          await supabase.functions.invoke("send-transactional-email", {
            body: {
              templateName: "appointment-cancelled",
              recipientEmail: patientEmail,
              idempotencyKey: `appt-cancelled-provider-${id}`,
              templateData: {
                recipientName: patientName,
                providerName,
                appointmentDate: startDate,
                appointmentTime: startTime,
                cancelReason: trimmedReason,
              },
            },
          });
        }
      }
    } else if (newStatus === "rescheduled") {
      toast({ title: "Appointment rescheduled" });
      if (appt?.patient_id) {
        await supabase.from("notifications").insert({
          user_id: appt.patient_id,
          title: "Appointment Rescheduled",
          message: "Your consultation has been rescheduled. Please check your dashboard for updated appointment details.",
          type: "appointment_rescheduled",
          related_id: id,
        });

        const { data: patientEmails } = await supabase.rpc("get_user_emails");
        const patientEmail = patientEmails?.find((e: any) => e.user_id === appt.patient_id)?.email;
        if (patientEmail) {
          const { data: providerProfile } = await supabase
            .from("profiles")
            .select("first_name, last_name, title_prefix")
            .eq("user_id", user!.id)
            .single();
          const providerName = providerProfile
            ? `${providerProfile.title_prefix || "Dr."} ${providerProfile.first_name || ""} ${providerProfile.last_name || ""}`.trim()
            : "Your provider";
          const patientName = appt.patient_first_name || undefined;
          const startDate = appt.start_time ? formatInTz(appt.start_time, tz, "MMMM d, yyyy") : undefined;
          const startTime = appt.start_time && appt.end_time
            ? `${formatInTz(appt.start_time, tz, "h:mm a")} – ${formatInTz(appt.end_time, tz, "h:mm a")} ${tzAbbr}`
            : undefined;

          await supabase.functions.invoke("send-transactional-email", {
            body: {
              templateName: "appointment-rescheduled",
              recipientEmail: patientEmail,
              idempotencyKey: `appt-rescheduled-provider-${id}`,
              templateData: {
                recipientName: patientName,
                providerName,
                rescheduledBy: "provider",
                appointmentDate: startDate,
                appointmentTime: startTime,
                bookingUrl: `${window.location.origin}/book`,
              },
            },
          });
        }
      }
    } else if (newStatus === "scheduled") {
      toast({ title: "Set back to pending/scheduled" });
    }

    fetchAppointments();
  };

  // Patients with an active (scheduled/confirmed, not yet ended) appointment —
  // used to detect "rescheduled" rows that haven't been replaced yet.
  const patientsWithActive = useMemo(() => {
    const set = new Set<string>();
    for (const a of appointments) {
      if (
        (a.status === "scheduled" || a.status === "confirmed") &&
        !isBefore(parseISO(a.end_time), new Date())
      ) {
        set.add(a.patient_id);
      }
    }
    return set;
  }, [appointments]);

  const upcoming = appointments.filter(
    (a) => (a.status === "scheduled" || a.status === "confirmed") && !isBefore(parseISO(a.end_time), new Date())
  );
  // Patient hit "Reschedule" but never picked a new slot — flag for provider follow-up.
  const awaitingReschedule = appointments.filter(
    (a) => a.status === "rescheduled" && !patientsWithActive.has(a.patient_id)
  );
  const awaitingRescheduleIds = new Set(awaitingReschedule.map((a) => a.id));
  const past = appointments.filter(
    (a) =>
      !awaitingRescheduleIds.has(a.id) &&
      ((a.status !== "scheduled" && a.status !== "confirmed") || isBefore(parseISO(a.end_time), new Date()))
  );

  if (appointments.length === 0) {
    return (
      <div>
        <h2 className="font-display text-xl text-foreground mb-4">Scheduled Consultations</h2>
        <Card className="border-border bg-card">
          <CardContent className="p-8 text-center">
            <CalendarDays size={40} className="mx-auto text-muted-foreground mb-3" />
            <p className="text-muted-foreground">No appointments scheduled yet.</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <h2 className="font-display text-xl text-foreground">Scheduled Consultations</h2>

      {upcoming.length > 0 && (
        <div className="space-y-3">
          {upcoming.map((a) => {
            const patientName = a.patient_first_name
              ? `${a.patient_first_name}${a.patient_last_name ? ` ${a.patient_last_name}` : ""}`
              : "Patient";
            return (
              <Card key={a.id} className={`bg-card ${a.status === "confirmed" ? "border-emerald-300" : "border-primary/20"}`}>
                <CardContent className="p-5">
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex items-start gap-3">
                      <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center shrink-0 mt-0.5">
                        <User size={18} className="text-primary" />
                      </div>
                      <div className="space-y-1">
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => { setDrawerPatientId(a.patient_id); setDrawerOpen(true); }}
                            className="font-medium text-foreground text-sm hover:text-primary hover:underline underline-offset-2 transition-colors text-left"
                          >
                            {patientName}
                          </button>
                          {a.status === "confirmed" && (
                            <Badge variant="outline" className="text-xs bg-emerald-100 text-emerald-800 border-emerald-200">
                              Confirmed
                            </Badge>
                          )}
                        </div>
                        <p className="text-sm text-muted-foreground">
                          <CalendarDays size={12} className="inline mr-1" />
                          {formatInTz(a.start_time, tz, "EEEE, MMMM d, yyyy")}
                        </p>
                        <p className="text-sm text-muted-foreground">
                          <Clock size={12} className="inline mr-1" />
                          {formatInTz(a.start_time, tz, "h:mm a")} – {formatInTz(a.end_time, tz, "h:mm a")} {tzAbbr}
                        </p>
                        {a.meeting_url && (
                          <a
                            href={a.meeting_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-1.5 text-xs font-medium text-primary hover:underline mt-1"
                          >
                            {a.call_type === "audio" ? <Phone size={12} /> : <Video size={12} />}
                            {a.call_type === "audio" ? "Join Audio Call" : "Join Video Call"}
                          </a>
                        )}
                      </div>
                    </div>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button
                          variant="outline"
                          size="sm"
                          className="gap-1.5 text-xs shrink-0"
                        >
                          Update Status <ChevronDown size={12} />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        {a.status !== "confirmed" && (
                          <DropdownMenuItem onClick={() => updateStatus(a.id, "confirmed")} className="gap-2">
                            <ShieldCheck size={14} className="text-emerald-600" /> Confirm
                          </DropdownMenuItem>
                        )}
                        <DropdownMenuItem onClick={() => setCompleteConfirmId(a.id)} className="gap-2">
                          <CheckCircle size={14} className="text-green-600" /> Complete
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => setNoShowConfirmId(a.id)} className="gap-2">
                          <XCircle size={14} className="text-red-600" /> No-Show
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => setRescheduleConfirmId(a.id)} className="gap-2">
                          <RotateCcw size={14} className="text-amber-600" /> Reschedule
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => setCancelConfirmId(a.id)} className="gap-2">
                          <XCircle size={14} className="text-destructive" /> Cancel
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {awaitingReschedule.length > 0 && (
        <div className="space-y-2">
          <p className="text-xs font-medium text-amber-700 uppercase tracking-wide mt-4">
            Awaiting Reschedule
          </p>
          {awaitingReschedule.map((a) => {
            const patientName = a.patient_first_name
              ? `${a.patient_first_name}${a.patient_last_name ? ` ${a.patient_last_name}` : ""}`
              : "Patient";
            return (
              <Card key={a.id} className="border-amber-300 bg-amber-50/50">
                <CardContent className="p-4 flex items-center justify-between gap-3">
                  <div className="flex items-start gap-3">
                    <div className="w-9 h-9 rounded-lg bg-amber-100 flex items-center justify-center shrink-0 mt-0.5">
                      <RotateCcw size={16} className="text-amber-700" />
                    </div>
                    <div className="space-y-0.5">
                      <button
                        onClick={() => { setDrawerPatientId(a.patient_id); setDrawerOpen(true); }}
                        className="text-sm text-foreground font-medium hover:text-primary hover:underline underline-offset-2 transition-colors text-left"
                      >
                        {patientName}
                      </button>
                      <p className="text-xs text-muted-foreground">
                        Original: {formatInTz(a.start_time, tz, "MMM d, yyyy")} · {formatInTz(a.start_time, tz, "h:mm a")} {tzAbbr}
                      </p>
                      <p className="text-xs text-amber-800">
                        Patient cancelled to reschedule but hasn't picked a new time yet.
                      </p>
                    </div>
                  </div>
                  <div className="flex flex-col items-end gap-2 shrink-0">
                    <Badge variant="outline" className="text-xs bg-amber-100 text-amber-800 border-amber-300">
                      Awaiting reschedule
                    </Badge>
                    <Button
                      size="sm"
                      variant="outline"
                      className="h-8 border-amber-400 text-amber-800 hover:bg-amber-100 hover:text-amber-900"
                      disabled={nudgingId === a.id}
                      onClick={() => nudgePatient(a)}
                    >
                      {nudgingId === a.id ? (
                        <Loader2 size={14} className="animate-spin mr-1.5" />
                      ) : (
                        <BellRing size={14} className="mr-1.5" />
                      )}
                      Nudge patient
                    </Button>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {past.length > 0 && (
        <div className="space-y-2">
          <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide mt-4">Past & Cancelled</p>
          {past.map((a) => {
            const config = statusConfig[a.status] || statusConfig.completed;
            const patientName = a.patient_first_name
              ? `${a.patient_first_name}${a.patient_last_name ? ` ${a.patient_last_name}` : ""}`
              : "Patient";
            const canRevert = a.status === "completed" || a.status === "no_show";
            return (
              <Card key={a.id} className="border-border bg-card/60">
                <CardContent className="p-4 flex items-center justify-between">
                  <div className="space-y-0.5">
                    <button
                      onClick={() => { setDrawerPatientId(a.patient_id); setDrawerOpen(true); }}
                      className="text-sm text-foreground font-medium hover:text-primary hover:underline underline-offset-2 transition-colors text-left"
                    >
                      {patientName}
                    </button>
                    <p className="text-sm text-muted-foreground">
                      {formatInTz(a.start_time, tz, "MMM d, yyyy")} · {formatInTz(a.start_time, tz, "h:mm a")} {tzAbbr}
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    {canRevert && (
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="sm" className="gap-1 text-xs h-7 px-2">
                            <RotateCcw size={12} /> Change
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => updateStatus(a.id, "scheduled")} className="gap-2">
                            <CalendarDays size={14} className="text-primary" /> Set as Pending
                          </DropdownMenuItem>
                          {a.status !== "completed" && (
                            <DropdownMenuItem onClick={() => setCompleteConfirmId(a.id)} className="gap-2">
                              <CheckCircle size={14} className="text-green-600" /> Complete
                            </DropdownMenuItem>
                          )}
                          {a.status !== "no_show" && (
                            <DropdownMenuItem onClick={() => setNoShowConfirmId(a.id)} className="gap-2">
                              <XCircle size={14} className="text-red-600" /> No-Show
                            </DropdownMenuItem>
                          )}
                        </DropdownMenuContent>
                      </DropdownMenu>
                    )}
                    <Badge variant="outline" className={`text-xs capitalize ${config.class}`}>
                      {config.label}
                    </Badge>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
      <AlertDialog open={!!noShowConfirmId} onOpenChange={(open) => !open && setNoShowConfirmId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Mark as No-Show?</AlertDialogTitle>
            <AlertDialogDescription>
              This will notify the patient that they missed their appointment and send them an email to reschedule. This action can be reverted later.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={() => {
                if (noShowConfirmId) updateStatus(noShowConfirmId, "no_show");
                setNoShowConfirmId(null);
              }}
            >
              Confirm No-Show
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      <AlertDialog open={!!completeConfirmId} onOpenChange={(open) => !open && setCompleteConfirmId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Mark as Completed?</AlertDialogTitle>
            <AlertDialogDescription>
              This will finalize the consultation and notify the patient. This action can be reverted later.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                if (completeConfirmId) updateStatus(completeConfirmId, "completed");
                setCompleteConfirmId(null);
              }}
            >
              Confirm Completion
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      <AlertDialog open={!!rescheduleConfirmId} onOpenChange={(open) => !open && setRescheduleConfirmId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Reschedule Appointment?</AlertDialogTitle>
            <AlertDialogDescription>
              {(() => {
                const appt = appointments.find((a) => a.id === rescheduleConfirmId);
                if (!appt) return "This will cancel the current appointment and notify the patient to book a new time.";
                const patientName = appt.patient_first_name
                  ? `${appt.patient_first_name}${appt.patient_last_name ? ` ${appt.patient_last_name}` : ""}`
                  : "the patient";
                return `This will cancel ${patientName}'s appointment on ${formatInTz(appt.start_time, tz, "MMMM d")} at ${formatInTz(appt.start_time, tz, "h:mm a")} ${tzAbbr} and notify them to book a new time. Would you like to proceed?`;
              })()}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Keep Current Appointment</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                if (rescheduleConfirmId) updateStatus(rescheduleConfirmId, "rescheduled");
                setRescheduleConfirmId(null);
              }}
            >
              Reschedule
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      <AlertDialog open={!!cancelConfirmId} onOpenChange={(open) => { if (!open) { setCancelConfirmId(null); setCancelReason(""); } }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Cancel Appointment?</AlertDialogTitle>
            <AlertDialogDescription>
              {(() => {
                const appt = appointments.find((a) => a.id === cancelConfirmId);
                if (!appt) return "This will cancel the appointment and notify the patient.";
                const patientName = appt.patient_first_name
                  ? `${appt.patient_first_name}${appt.patient_last_name ? ` ${appt.patient_last_name}` : ""}`
                  : "the patient";
                return `This will cancel ${patientName}'s appointment on ${formatInTz(appt.start_time, tz, "MMMM d")} at ${formatInTz(appt.start_time, tz, "h:mm a")} ${tzAbbr} and notify them by email. This action cannot be undone.`;
              })()}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="space-y-2">
            <Label htmlFor="cancel-reason" className="text-sm">Reason for cancellation <span className="text-muted-foreground font-normal">(optional)</span></Label>
            <Textarea
              id="cancel-reason"
              value={cancelReason}
              onChange={(e) => setCancelReason(e.target.value.slice(0, 500))}
              placeholder="e.g. Provider unavailable, patient request, scheduling conflict…"
              rows={3}
              maxLength={500}
            />
            <p className="text-xs text-muted-foreground">Shared with the patient in their cancellation email and saved to the appointment record.</p>
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel>Keep Appointment</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={() => {
                if (cancelConfirmId) updateStatus(cancelConfirmId, "cancelled", cancelReason);
                setCancelConfirmId(null);
                setCancelReason("");
              }}
            >
              Cancel Appointment
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      <PatientConsultDrawer
        patientId={drawerPatientId}
        open={drawerOpen}
        onOpenChange={setDrawerOpen}
      />
    </div>
  );
};

export default ProviderAppointments;
